/**
 * Operation execution status — colors for BMR Tracking / DAG / tables.
 * Palette chosen so adjacent legend swatches are easy to tell apart.
 *
 * Critical supervisor approval: "Completed - Under Review" blocks downstream deps.
 * Non-critical: "Completed - Review Pending" — deps may proceed; supervisor reviews later.
 */

export const EXECUTION_STATUS_STYLES = {
  // ux-production-supervisor #4: "Pending" (nobody has started this) used to be the SAME red as
  // "Hold" (this is blocked), so on the batch table a step waiting its turn looked identical to a
  // step that had stopped the batch. The Gantt was fixed first — grey for not-started, red reserved
  // for blocked — and drilling from it into this table then broke the lesson it had just taught.
  // Grey here matches BatchGantt's `Pending: defaultColors.palette.gray[400]` exactly.
  Pending: {
    label: 'Pending',
    tagColor: 'default',
    color: '#94a3b8',
    bg: '#f8fafc',
    fillBg: '#e2e8f0',
    border: '#94a3b8',
    text: '#475569',
    filter:
      'brightness(0) saturate(100%) invert(72%) sepia(9%) saturate(608%) hue-rotate(176deg) brightness(93%) contrast(88%)',
  },
  Running: {
    label: 'Running',
    tagColor: 'processing',
    color: '#2563eb',
    bg: '#eff6ff',
    fillBg: '#bfdbfe',
    border: '#2563eb',
    text: '#1e3a8a',
    filter:
      'brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(1752%) hue-rotate(211deg) brightness(97%) contrast(96%)',
  },
  Completed: {
    label: 'Completed',
    tagColor: 'success',
    color: '#16a34a',
    bg: '#f0fdf4',
    fillBg: '#bbf7d0',
    border: '#16a34a',
    text: '#14532d',
    filter:
      'brightness(0) saturate(100%) invert(48%) sepia(100%) saturate(362%) hue-rotate(82deg) brightness(91%) contrast(90%)',
  },
  CompletedUnderReview: {
    label: 'Completed - Under Review',
    tagColor: 'purple',
    color: '#7c3aed',
    bg: '#f5f3ff',
    fillBg: '#ddd6fe',
    border: '#7c3aed',
    text: '#4c1d95',
    filter:
      'brightness(0) saturate(100%) invert(28%) sepia(89%) saturate(3500%) hue-rotate(252deg) brightness(95%) contrast(95%)',
  },
  CompletedReviewPending: {
    label: 'Completed - Review Pending',
    tagColor: 'cyan',
    color: '#0891b2',
    bg: '#ecfeff',
    fillBg: '#a5f3fc',
    border: '#0891b2',
    text: '#155e75',
    filter:
      'brightness(0) saturate(100%) invert(52%) sepia(85%) saturate(1200%) hue-rotate(155deg) brightness(92%) contrast(92%)',
  },
  CompletedReviewed: {
    label: 'Completed - Reviewed',
    tagColor: 'green',
    color: '#0f766e',
    bg: '#f0fdfa',
    fillBg: '#99f6e4',
    border: '#0f766e',
    text: '#134e4a',
    filter:
      'brightness(0) saturate(100%) invert(38%) sepia(45%) saturate(1200%) hue-rotate(130deg) brightness(90%) contrast(92%)',
  },
  Paused: {
    label: 'Paused',
    tagColor: 'gold',
    color: '#ca8a04',
    bg: '#fefce8',
    fillBg: '#fef08a',
    border: '#ca8a04',
    text: '#713f12',
    filter:
      'brightness(0) saturate(100%) invert(72%) sepia(85%) saturate(1100%) hue-rotate(1deg) brightness(95%) contrast(95%)',
  },
  Hold: {
    label: 'Hold',
    tagColor: 'error',
    color: '#be123c',
    bg: '#fff1f2',
    fillBg: '#fecdd3',
    border: '#be123c',
    text: '#881337',
    filter:
      'brightness(0) saturate(100%) invert(22%) sepia(95%) saturate(4000%) hue-rotate(328deg) brightness(90%) contrast(95%)',
  },
  Skipped: {
    label: 'Skipped',
    tagColor: 'default',
    color: '#64748b',
    bg: '#f1f5f9',
    fillBg: '#cbd5e1',
    border: '#64748b',
    text: '#334155',
    filter:
      'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(900%) hue-rotate(176deg) brightness(92%) contrast(86%)',
  },
};

/** Legend rows for BMR Tracking / DAG (order matches typical workflow). */
export const EXECUTION_STATUS_LEGEND = [
  { key: 'Pending', description: 'Not started' },
  { key: 'Running', description: 'In progress' },
  { key: 'CompletedUnderReview', description: 'Done — awaiting supervisor (blocks next op if critical)' },
  { key: 'CompletedReviewPending', description: 'Done — supervisor review open (non-critical, deps may run)' },
  { key: 'Completed', description: 'Done — no supervisor gate' },
  { key: 'CompletedReviewed', description: 'Done — supervisor approved' },
  { key: 'Paused', description: 'Paused' },
  { key: 'Hold', description: 'Batch on hold' },
];

/**
 * @param {string|object} statusOrRow - raw status string or API row
 * @returns {{ key: string, label: string, sublabel?: string, raw: string, style: object }}
 */
export function resolveOperationExecutionStatus(statusOrRow) {
  const row =
    typeof statusOrRow === 'object' && statusOrRow !== null ? statusOrRow : { status: statusOrRow };
  const raw = String(row.status || row.operation_status || '').trim();
  const lower = raw.toLowerCase();

  let key = 'Pending';
  let sublabel;
  let matched = true;

  if (
    lower === 'pending approval' ||
    lower.includes('under review') ||
    row.downstream_blocked === true ||
    row.downstream_blocked === 'true'
  ) {
    key = 'CompletedUnderReview';
  } else if (lower.includes('review pending')) {
    key = 'CompletedReviewPending';
  } else if (lower.includes('reviewed')) {
    key = 'CompletedReviewed';
  } else if (
    row.supervisor_review_pending === true ||
    row.supervisor_review_pending === 'true' ||
    (row.is_supervisor_approval_required &&
      String(row.approval_status || '').toUpperCase() === 'PENDING' &&
      String(row.batch_operation_status || '').toLowerCase() === 'completed')
  ) {
    key = 'CompletedReviewPending';
  } else if (lower.includes('hold')) {
    key = 'Hold';
  } else if (lower.includes('pause')) {
    key = 'Paused';
  } else if (lower.includes('skip')) {
    key = 'Skipped';
  } else if (lower.includes('run') || lower.includes('progress')) {
    key = 'Running';
  } else if (lower.includes('complet') || lower.includes('done')) {
    key = 'Completed';
  } else if (lower.includes('pend')) {
    key = 'Pending';
  } else {
    matched = false;
  }

  const style = EXECUTION_STATUS_STYLES[key] || EXECUTION_STATUS_STYLES.Pending;

  if (!sublabel && row.reviewed_by) {
    const who = String(row.reviewed_by).trim();
    const at = row.reviewed_at ? String(row.reviewed_at).trim() : '';
    sublabel = at ? `${who} · ${at}` : who;
  } else if (!sublabel && key === 'CompletedReviewPending') {
    sublabel = 'Awaiting supervisor review';
  }

  // EXV-026: a word this map does not know (the server's "Not Started", …) is shown as the server wrote it, in the
  // neutral style — never relabelled "Pending".
  return {
    key,
    label: !matched && raw ? raw : style.label,
    sublabel,
    raw: raw || style.label,
    style,
  };
}

export function getExecutionStatusConfig(key) {
  return EXECUTION_STATUS_STYLES[key] || EXECUTION_STATUS_STYLES.Pending;
}

/** Ant Design Tag color for batch/operation status columns. */
export function getExecutionStatusTagColor(statusOrRow) {
  return resolveOperationExecutionStatus(statusOrRow).style.tagColor;
}

/** Map API / Batch_Config status to DAG node status key (ArchiMateFlow uses style keys). */
export function toDagNodeStatus(statusOrRow) {
  const { key, label } = resolveOperationExecutionStatus(statusOrRow);
  if (key === 'Running') return 'In Progress';
  return label;
}

/* ---------------------------------------------------------------------------
 * Batch status — the values `Batch_Size_Info.batch_status` actually carries on the backend
 * (Pending, Scheduled, Running, In Progress, Hold, Completed, Released, Terminated; the live
 * monitor also maps v2 states to Aborted). They are not operation statuses, so "In Progress" and
 * "Scheduled" used to fall through to the Pending grey. Same shape as EXECUTION_STATUS_STYLES
 * ({ label, color, bg, border, text }) so a badge renders either the same way; reuses those
 * entries where the meaning is the same.
 * ------------------------------------------------------------------------ */
const BATCH_STATUS_STYLES = {
  pending: EXECUTION_STATUS_STYLES.Pending,
  scheduled: { label: 'Scheduled', tagColor: 'geekblue', color: '#4f46e5', bg: '#eef2ff', fillBg: '#c7d2fe', border: '#6366f1', text: '#3730a3' },
  running: EXECUTION_STATUS_STYLES.Running,
  inprogress: { ...EXECUTION_STATUS_STYLES.Running, label: 'In Progress' },
  hold: EXECUTION_STATUS_STYLES.Hold,
  paused: EXECUTION_STATUS_STYLES.Paused,
  completed: EXECUTION_STATUS_STYLES.Completed,
  released: EXECUTION_STATUS_STYLES.CompletedReviewed ? { ...EXECUTION_STATUS_STYLES.CompletedReviewed, label: 'Released' } : EXECUTION_STATUS_STYLES.Completed,
  terminated: { label: 'Terminated', tagColor: 'error', color: '#b91c1c', bg: '#fef2f2', fillBg: '#fecaca', border: '#b91c1c', text: '#7f1d1d' },
  aborted: { label: 'Aborted', tagColor: 'error', color: '#b91c1c', bg: '#fef2f2', fillBg: '#fecaca', border: '#b91c1c', text: '#7f1d1d' },
  rejected: { label: 'Rejected', tagColor: 'error', color: '#b91c1c', bg: '#fef2f2', fillBg: '#fecaca', border: '#b91c1c', text: '#7f1d1d' },
  cancelled: { label: 'Cancelled', tagColor: 'default', color: '#64748b', bg: '#f1f5f9', fillBg: '#e2e8f0', border: '#94a3b8', text: '#334155' },
};
const UNKNOWN_BATCH_STATUS = { label: '', tagColor: 'default', color: '#64748b', bg: '#f8fafc', fillBg: '#e2e8f0', border: '#cbd5e1', text: '#475569' };

/**
 * Colours for a batch status string from the backend. Case, spaces, hyphens and underscores are
 * ignored ("In Progress" = "IN_PROGRESS"). A status this map does not know renders neutral with
 * its own text — never as "Pending".
 */
export function getBatchStatusStyle(status) {
  const raw = String(status ?? '').trim();
  const known = BATCH_STATUS_STYLES[raw.toLowerCase().replace(/[\s_-]+/g, '')];
  return known ? { ...known, raw } : { ...UNKNOWN_BATCH_STATUS, label: raw || '—', raw };
}
